
    // IMPORTS
    import { computed, defineComponent } from 'vue'
    import router from '@/router'

    import { useEquipmentStore } from '@/store/equipment'
    import { useUserStore } from '@/store/user'
    import { buildMaterialLocalization } from '@/util'

    import LoaderGuard from '@/components/common/loader-guard.vue'
    import Grid from '@/components/grid/my-grid.vue'
    import MyIcon from '@/components/common/my-icon.vue'

    import { GridColumnProps } from '@progress/kendo-vue-grid'
    import { SortDescriptor } from '@progress/kendo-data-query'
    import { IBomDeviationDTO } from '@/types/bom'


    interface IBomDeviationRow {
        status: string,
        materialNumber: number,
        description: string,
        poBomQty: number,
        equipmentBomQty: number
    }

    export default defineComponent({
        name: 'equipmentDeviations',

        components: {
            LoaderGuard,
            Grid,
            MyIcon
        },

        setup() {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const userStore = useUserStore()
            const equipmentStore = useEquipmentStore()

            if (!userStore.isAdmin) {
                router.replace({ name: 'equipment' })
            }

            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const publicData = {}

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const bomDeviations = computed(() => equipmentStore.bomDeviations)
            const hasLoaded = computed(() => bomDeviations.value != null)
            const dataItems = computed(() => transform(bomDeviations.value))

            const columns = computed(() => [
                {
                    field: 'status',
                    title: localize('status'),
                    cell: 'custom1',
                    width: 150,
                },
                {
                    field: 'materialNumber',
                    title: localize('materialNo'),
                    width: 150
                },
                {
                    field: 'description',
                    title: localize('description'),
                },
                {
                    field: 'poBomQty',
                    title: `${localize('qty')} (${localize('ProductionOrder')})`,
                    cell:  'rightJustified',
                    width: 200
                },
                {
                    field: 'equipmentBomQty',
                    title: `${localize('qty')} (${localize('equipment')})`,
                    cell: 'rightJustified',
                    width: 200
                }
            ] as GridColumnProps[])

            const initialSort = computed<SortDescriptor[]>(() => [
                { field: 'status', dir: 'asc' },
                { field: 'materialNumber', dir: 'asc' }
            ])

            const computedData = {
                hasLoaded,
                dataItems,
                columns,
                initialSort
            }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize, materialTextFor } = buildMaterialLocalization()

            const publicMethods = { localize }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/

            function transform(deviations: IBomDeviationDTO[] | null): IBomDeviationRow[] | null {
                if (deviations == null) {
                    return null
                }

                return deviations.map<IBomDeviationRow>((deviation) => ({
                    status: getStatusText(deviation),
                    materialNumber: deviation.material.materialNo,
                    description: materialTextFor(deviation.material),
                    poBomQty: deviation.poBomQty,
                    equipmentBomQty: deviation.equipmentBomQty
                })
                )
            }

            function getStatusText(deviation: IBomDeviationDTO): string {
                if (deviation.poBomQty > 0 && deviation.equipmentBomQty == 0) {
                    return 'removed'
                }
                else if (deviation.poBomQty == 0 && deviation.equipmentBomQty > 0) {
                    return 'added'
                }
                else if (deviation.poBomQty != deviation.equipmentBomQty) {
                    return 'changed'
                }
                else {
                    return ''
                }
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
